import * as React from 'react';
import { createFileRoute, useNavigate, useSearch } from '@tanstack/react-router';
import { Button } from '../../../../components/ui/Button.js';
import { useToast } from '../../../../components/ui/Toast.js';
import { Plus, Calendar, ShieldAlert } from 'lucide-react';
import { useDeleteHoliday, type Holiday } from '../../../../features/settings/network/holidays.api';
import { PageHeader } from '../../../../components/layout/PageHeader';
import { usePageTitle } from '../../../../lib/hooks';
import { defineListPage, useListPage } from '@repo/list';
import type { PaginatedQueryFn } from '@repo/list';
import { DataTable, createUrlAdapter } from '@repo/web-list';
import { fetcher } from '../../../../lib/api-fetcher';

export const Route = createFileRoute('/_auth/settings/holidays/')({
  component: HolidaysSettingsPage,
});

const holidaysConfig = defineListPage<Holiday>({
  variant: 'data-table',
  resource: 'holidays',
  searchable: true,
  columns: [
    { id: 'name', header: 'Holiday Name', accessor: 'name', sortable: true },
    { id: 'date', header: 'Scheduled Date', accessor: 'date' },
    { id: 'isRecurring', header: 'Type', accessor: 'isRecurring' },
  ],
  defaultPageSize: 100,
});

const fetchHolidays: PaginatedQueryFn<Holiday> = async (params) => {
  const res = await fetcher.get<{ data: Holiday[] } | Holiday[]>('/staff/v1/config/holidays');
  const all = Array.isArray(res) ? res : (res?.data ?? []);
  const search = params.search?.toLowerCase();
  const searched = search
    ? all.filter((h) => h.name.toLowerCase().includes(search) || h.date.includes(search))
    : all;
  return {
    data: searched,
    meta: { total: searched.length, page: 1, limit: 100 },
  };
};

function HolidaysSettingsPage() {
  usePageTitle('Holidays Settings');
  const navigate = useNavigate();
  const search = useSearch({ strict: false });
  const { toast } = useToast();
  const deleteHoliday = useDeleteHoliday();

  const urlAdapter = React.useMemo(
    () => createUrlAdapter(search, navigate as never),
    [search, navigate],
  );

  const list = useListPage(holidaysConfig, fetchHolidays, urlAdapter);

  const handleDeleteHoliday = async (id: string) => {
    try {
      await deleteHoliday.mutateAsync(id);
      toast({
        type: 'success',
        title: 'Success',
        description: 'Holiday deleted successfully.',
      });
      void list.refetch();
    } catch (err) {
      console.error('Failed to delete holiday:', err);
      toast({
        type: 'error',
        title: 'Error',
        description: 'Failed to delete holiday. Please try again.',
      });
    }
  };

  return (
    <div className="flex flex-col w-full h-full" style={{ viewTransitionName: 'page-body' }}>
      <PageHeader
        title="Institution Holidays"
        subtitle="Configure institutional holidays. Due dates and grace-period calculations for auctions automatically shift to the next business day."
        action={
          <Button variant="primary" onClick={() => navigate({ to: '/settings/holidays/new' })}>
            <Plus className="w-4 h-4 mr-2" />
            Add Holiday
          </Button>
        }
      />
      
      <div className="flex-1 w-full min-h-0">
        <div className="bg-surface-2/40 border border-border rounded-xl p-4 flex items-start gap-3 max-w-3xl mb-6">
          <ShieldAlert className="w-5 h-5 text-accent-600 shrink-0 mt-0.5" />
          <div className="text-xs text-text-secondary leading-relaxed">
            <span className="font-bold text-text-primary">Calculation Policy:</span> Any auction installment or running receipt deadline falling on a listed holiday automatically shifts to the next working day. Recurring holidays will carry over to subsequent fiscal years automatically.
          </div>
        </div>

        <div className="max-w-4xl">
          <DataTable
            config={holidaysConfig}
            list={list}
            rowActions={[
              {
                label: 'Delete Holiday',
                variant: 'destructive',
                requiresConfirmation: true,
                onClick: (row) => void handleDeleteHoliday(row.id),
              },
            ]}
            customRenderers={{
              name: (row) => (
                <div className="flex items-center gap-2">
                  <Calendar className="w-4 h-4 text-accent-500 shrink-0" />
                  <span className="font-semibold">{row.name}</span>
                </div>
              ),
              date: (row) => (
                <span className="font-mono">
                  {new Date(row.date).toLocaleDateString('en-IN', {
                    weekday: 'long',
                    day: '2-digit',
                    month: 'long',
                    year: 'numeric',
                  })}
                </span>
              ),
              isRecurring: (row) => (
                row.isRecurring ? (
                  <span className="text-xs font-bold text-success-text bg-success-bg px-2.5 py-0.5 rounded-full border border-success-bg">
                    Annual Recurring
                  </span>
                ) : (
                  <span className="text-xs font-bold text-text-secondary bg-surface-3 px-2.5 py-0.5 rounded-full border border-border">
                    One-time Only
                  </span>
                )
              ),
            }}
          />
        </div>
      </div>
    </div>
  );
}
